<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useQuery } from '@tanstack/vue-query'
import { api } from '@/lib/api'
import { can, logout, session } from '@/lib/auth'
import { LOCALES, setLocale, type Locale } from '@/lib/i18n'
import { effectiveTheme, nextTheme, onSystemThemeChange, setTheme, theme } from '@/lib/theme'
import Avatar from '@/ui/Avatar.vue'
import Button from '@/ui/Button.vue'
import DropdownMenu, { type MenuChoice, type MenuEntry } from '@/ui/DropdownMenu.vue'
import Icon from '@/ui/Icon.vue'
import Tooltip from '@/ui/Tooltip.vue'
import NotificationCenter from '@/features/gamification/NotificationCenter.vue'
import QuickSearch from './QuickSearch.vue'

defineProps<{ navOpen: boolean }>()
const emit = defineEmits<{ openNav: []; help: [] }>()
const { t, locale } = useI18n()
const router = useRouter()


/* ---- theme, following the OS while on "system" ---- */
const systemTick = ref(0)
const shown = computed(() => { void systemTick.value; return effectiveTheme(theme.value) })
function cycleTheme() { setTheme(nextTheme(theme.value)) }
let stopWatchingOs = () => {}
onMounted(() => {
  stopWatchingOs = onSystemThemeChange(() => { systemTick.value++ })
})
onUnmounted(() => { stopWatchingOs() })

/* ---- language ---- */
const languages = computed<MenuChoice[]>(() => LOCALES.map((l: Locale) => ({ value: l, label: t(`shell.language.${l}`) })))

/* ---- unacknowledged incidents ---- */
const incidents = useQuery({
  queryKey: ['system', 'incidents', 'open'],
  queryFn: () => api<{ incidents: Array<{ severity: string; acknowledgedAt: string | null }> }>('/system/incidents'),
  refetchInterval: 30_000,
  enabled: computed(() => can('system.diagnostics.view')),
})
const open = computed(() => (incidents.data.value?.incidents ?? []).filter((i) => !i.acknowledgedAt))
const critical = computed(() => open.value.some((i) => i.severity === 'critical'))

/* ---- account ---- */
const account = computed<MenuEntry[]>(() => [
  { label: session.user?.extension ? t('shell.account.sip', { ext: session.user.extension }) : t('shell.account.noSip'),
    icon: 'headphones', disabled: true, onSelect: () => {} },
  { separator: true },
  { label: t('shell.account.profile'), icon: 'user', onSelect: () => { void router.push('/profile') } },
  { label: t('shell.account.preferences'), icon: 'settings', onSelect: () => { void router.push('/profile') } },
  { label: t(`shell.theme.${theme.value}`), icon: 'moon', onSelect: cycleTheme },
  { label: t('shell.help.button'), icon: 'help', onSelect: () => emit('help') },
  { separator: true },
  // An explicit logout forgets the address: the next account on this workstation starts from its own home.
  { label: t('shell.account.logout'), icon: 'log-out', danger: true, onSelect: () => { void logout().then(() => router.replace('/')) } },
])
const roleLabel = computed(() => {
  if (!session.user) return ''
  const role = t(`roles.${session.user.role}`, session.user.role)
  return session.user.extension ? `${role} · ${session.user.extension}` : role
})
</script>

<template>
  <div class="sticky top-0 z-topbar flex h-topbar min-w-0 items-center gap-2 border-b border-border bg-card/95 px-4 backdrop-blur max-sm:gap-1 max-sm:px-2">
    <!-- z-topbar: above the tables' sticky headers (z-sticky), below drawers and dialogs. -->
    <Button id="shell-burger" variant="icon" class="lg:hidden" icon-start="menu" :aria-label="t('shell.openMenu')"
            :aria-expanded="navOpen" aria-controls="shell-nav" @click="emit('openNav')" />

    <QuickSearch v-if="can('contact.read')" />
    <div class="flex-1" />

    <!-- On a phone the bar has no room for everything: help and theme stay in the account menu. -->
    <Tooltip :text="t('shell.help.button')" side="bottom">
      <Button variant="icon" class="max-sm:hidden" icon-start="help" :aria-label="t('shell.help.button')" @click="emit('help')" />
    </Tooltip>

    <DropdownMenu :choices="languages" :model-value="locale" :width="160" @update:model-value="(l) => setLocale(l as Locale, true)">
      <Button variant="icon" icon-start="globe" :aria-label="t('shell.language.label')" />
    </DropdownMenu>

    <Tooltip :text="t(`shell.theme.${theme}`)" side="bottom">
      <Button variant="icon" class="max-sm:hidden" :icon-start="shown === 'dark' ? 'sun' : 'moon'" :aria-label="t(`shell.theme.${theme}`)" @click="cycleTheme">
        <span v-if="theme === 'system'" class="absolute bottom-1.5 end-1.5 h-[5px] w-[5px] rounded-pill bg-primary" aria-hidden="true" />
      </Button>
    </Tooltip>

    <NotificationCenter />

    <Tooltip v-if="can('system.diagnostics.view')" :text="open.length ? t('shell.incidents.count', { count: open.length }, open.length) : t('shell.incidents.none')" side="bottom">
      <Button variant="icon" to="/system/incidents" icon-start="alert-circle"
              :aria-label="open.length ? t('shell.incidents.count', { count: open.length }, open.length) : t('shell.incidents.none')">
        <span v-if="open.length" :class="[critical ? 'bg-danger' : 'bg-warning', 'absolute end-2 top-2 h-1.5 w-1.5 rounded-pill']" aria-hidden="true" />
      </Button>
    </Tooltip>

    <span class="mx-1 h-6 w-px bg-border max-sm:hidden" aria-hidden="true" />

    <DropdownMenu :items="account" :width="260">
      <button type="button" class="flex items-center gap-2 rounded-input px-1.5 py-1 hover:bg-muted" :aria-label="session.user?.fullName">
        <Avatar :name="session.user?.fullName ?? ''" />
        <span class="grid text-start leading-tight max-md:hidden">
          <span class="text-[13px] font-medium">{{ session.user?.fullName }}</span>
          <span class="text-[11.5px] text-muted-foreground">{{ roleLabel }}</span>
        </span>
        <Icon name="chevron-down" class="text-muted-foreground max-md:hidden" />
      </button>
    </DropdownMenu>
  </div>
</template>
